// =============================================================================
// notifications-client.http.ts — Adaptateur HTTP RÉEL du NotificationsClient (R15-COM-06).
// =============================================================================
// Câble la SPA contre les routes @SupplierRoute du centre de notifications :
//   • GET  /api/communication/notifications/summary            (cloche + widget D-04)
//   • GET  /api/communication/notifications?cursor=<opaque>     (volet consolidé, keyset, EXCLUT le chat)
//   • POST /api/communication/notifications/read  { ids }       (auto-read en LOT, DBU l.3252) — MUTATION ⇒ CSRF
//
// DBU D-1/D-2 : les routes `PATCH /:id/read` (clic) et `POST /read-all` (tout marquer) ont été SUPPRIMÉES
// côté serveur — elles ne sont plus appelées. Le seul marquage est le LOT auto-read à l'affichage du volet.
//
// SÉCURITÉ : session fournisseur = COOKIE httpOnly `supplier_session` (credentials: 'include') ; MUTATION
// ⇒ double-submit CSRF — on ré-émet le cookie NON-httpOnly `supplier_csrf` (PAS `admin_csrf`) en en-tête
// `x-csrf-token` (parité rate-collection-client.http). AUCUNE règle métier ici : appel, parse, map erreur.
// MAPPING ERREURS par STATUS HTTP (401/403/404), message serveur relayé.

import {
  NotificationApiError,
  type NotificationApiErrorKind,
  type NotificationsClient,
  type NotificationPageDto,
  type NotificationSummaryDto,
  type NotificationUnreadCountDto,
} from '@/api/notifications-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login FOURNISSEUR (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'supplier_csrf';

/** Mapping STATUS HTTP → kind UX (401 session, 403 garde, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, NotificationApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/** Lit la valeur du cookie CSRF `supplier_csrf` (NON-httpOnly). `undefined` si absent (fail-closed serveur). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API notifications fournisseur. `baseUrl` par défaut `/api`
 * (parité des autres adaptateurs : la SPA et l'API partagent l'origine, ou un reverse proxy route `/api/*`).
 */
export class HttpNotificationsClient implements NotificationsClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getSummary(): Promise<NotificationSummaryDto> {
    const response = await fetch(
      `${this.baseUrl}/communication/notifications/summary`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as NotificationSummaryDto;
  }

  public async listPage(cursor?: string): Promise<NotificationPageDto> {
    // `cursor` absent ⇒ première page (aucun query param). Sinon on transmet le curseur opaque tel quel.
    const query =
      cursor !== undefined && cursor.length > 0
        ? `?cursor=${encodeURIComponent(cursor)}`
        : '';
    const response = await fetch(
      `${this.baseUrl}/communication/notifications${query}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as NotificationPageDto;
  }

  public async markRead(
    ids: readonly string[],
  ): Promise<NotificationUnreadCountDto> {
    const response = await fetch(
      `${this.baseUrl}/communication/notifications/read`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.mutationHeaders(),
        // Lot des ids AFFICHÉS non-lus (auto-read à l'affichage du volet, DBU l.3252). Le serveur borne
        // (tableau, ≤ 500, strings) et recalcule le badge (autorité).
        body: JSON.stringify({ ids }),
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as NotificationUnreadCountDto;
  }

  /** En-têtes des MUTATIONS : content-type + double-submit CSRF (`x-csrf-token` = cookie `supplier_csrf`). */
  private mutationHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    return headers;
  }

  private async toApiError(response: Response): Promise<NotificationApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new NotificationApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
